// Presets d'endpoints API disponibles
export const API_ENDPOINTS = [
  {
    id: 'conversations_summary',
    name: 'Résumé des conversations',
    endpoint: '/api/v2/accounts/{accountId}/reports/summary',
    method: 'GET',
    description: 'Métriques globales des conversations',
    category: 'conversations',
    params: [
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        options: ['account', 'agent', 'inbox', 'team', 'label'],
        default: 'account',
      },
      { key: 'id', label: 'ID (optionnel)', type: 'text', optional: true },
      {
        key: 'since',
        label: 'Depuis (timestamp)',
        type: 'number',
        default: () => Math.floor(Date.now() / 1000) - 86400 * 30,
      },
      {
        key: 'until',
        label: "Jusqu'à (timestamp)",
        type: 'number',
        default: () => Math.floor(Date.now() / 1000),
      },
    ],
    dataMapper: {
      metric: {
        value: data => {
          const count =
            data?.conversations_count || data?.data?.conversations_count || 0;
          return formatNumber(count);
        },
        subvalue: data => {
          const current =
            data?.conversations_count || data?.data?.conversations_count || 0;
          const previous = data?.previous?.conversations_count || 0;
          if (previous === 0) return null;
          const diff = ((current - previous) / previous) * 100;
          return `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}%`;
        },
        trend: data => {
          const current =
            data?.conversations_count || data?.data?.conversations_count || 0;
          const previous = data?.previous?.conversations_count || 0;
          if (current > previous) return 'up';
          if (current < previous) return 'down';
          return 'neutral';
        },
      },
      chart: {
        series: data => {
          // Si timeseries existe, l'utiliser
          if (data?.timeseries && Array.isArray(data.timeseries)) {
            return [
              {
                name: 'Conversations',
                data: data.timeseries.map(
                  item => item.value || item.count || 0
                ),
              },
            ];
          }
          // Sinon, créer une série simple
          return [
            {
              name: 'Conversations',
              data: [
                data?.conversations_count ||
                  data?.data?.conversations_count ||
                  0,
              ],
            },
          ];
        },
        categories: data => {
          if (data?.timeseries && Array.isArray(data.timeseries)) {
            return data.timeseries.map(item => {
              if (item.date) return formatDate(item.date);
              if (item.timestamp) return formatDate(item.timestamp);
              return item.label || '';
            });
          }
          return ['Total'];
        },
      },
    },
  },
  {
    id: 'live_conversations',
    name: 'Conversations en direct',
    endpoint: '/api/v2/accounts/{accountId}/live_reports/conversation_metrics',
    method: 'GET',
    description: 'Nombre de conversations ouvertes, en attente, etc.',
    category: 'conversations',
    params: [
      {
        key: 'team_id',
        label: 'ID Équipe (optionnel)',
        type: 'text',
        optional: true,
      },
    ],
    dataMapper: {
      metric: {
        value: data => formatNumber(data?.open || 0),
        subvalue: data => {
          const unattended = data?.unattended || 0;
          return unattended > 0 ? `${unattended} non assignées` : null;
        },
        trend: () => 'neutral', // Pas de comparaison temporelle pour les données live
      },
      chart: {
        series: data => [
          { name: 'Ouvertes', data: [data?.open || 0] },
          { name: 'En attente', data: [data?.pending || 0] },
          { name: 'Non assignées', data: [data?.unattended || 0] },
          { name: 'Non assignées', data: [data?.unassigned || 0] },
        ],
        categories: () => ['Maintenant'],
      },
    },
  },
  {
    id: 'conversations_timeseries',
    name: 'Série temporelle des conversations',
    endpoint: '/api/v2/accounts/{accountId}/reports',
    method: 'GET',
    description: 'Évolution des conversations dans le temps',
    category: 'conversations',
    params: [
      {
        key: 'type',
        label: 'Type',
        type: 'select',
        options: ['account', 'agent', 'inbox'],
        default: 'account',
      },
      {
        key: 'metric',
        label: 'Métrique',
        type: 'select',
        options: [
          'conversations_count',
          'avg_first_response_time',
          'avg_resolution_time',
        ],
        default: 'conversations_count',
      },
      {
        key: 'since',
        label: 'Depuis (timestamp)',
        type: 'number',
        default: () => Math.floor(Date.now() / 1000) - 86400 * 30,
      },
      {
        key: 'until',
        label: "Jusqu'à (timestamp)",
        type: 'number',
        default: () => Math.floor(Date.now() / 1000),
      },
      {
        key: 'group_by',
        label: 'Grouper par',
        type: 'select',
        options: ['day', 'week', 'month'],
        default: 'day',
        optional: true,
      },
    ],
    dataMapper: {
      chart: {
        series: data => {
          if (!Array.isArray(data)) return [{ name: 'Données', data: [] }];

          // Si c'est un array de séries
          return data.map((item, index) => ({
            name: item.metric || item.name || `Série ${index + 1}`,
            data: Array.isArray(item.values)
              ? item.values.map(v =>
                  typeof v === 'object' ? v.value || v.count || 0 : v
                )
              : [item.value || item.count || 0],
          }));
        },
        categories: data => {
          if (!Array.isArray(data) || data.length === 0) return [];

          const firstItem = data[0];
          if (Array.isArray(firstItem.values)) {
            return firstItem.values.map(v => {
              if (typeof v === 'object') {
                return v.date ? formatDate(v.date) : v.label || '';
              }
              return '';
            });
          }

          return data.map((item, index) => {
            if (item.date) return formatDate(item.date);
            if (item.timestamp) return formatDate(item.timestamp);
            return item.label || `Point ${index + 1}`;
          });
        },
      },
    },
  },
  {
    id: 'contacts_count',
    name: 'Nombre de contacts',
    endpoint: '/api/v1/accounts/{accountId}/contacts',
    method: 'GET',
    description: 'Total des contacts',
    category: 'contacts',
    params: [
      {
        key: 'page',
        label: 'Page',
        type: 'number',
        default: 1,
        optional: true,
      },
    ],
    dataMapper: {
      metric: {
        value: data => {
          const count =
            data?.meta?.count || data?.payload?.length || data?.length || 0;
          return formatNumber(count);
        },
        subvalue: data => {
          const total = data?.meta?.count || 0;
          const currentPage = data?.payload?.length || 0;
          return total > currentPage ? `${currentPage} sur ${total}` : null;
        },
        trend: () => 'neutral',
      },
    },
  },
  {
    id: 'conversations_list',
    name: 'Liste des conversations',
    endpoint: '/api/v1/accounts/{accountId}/conversations',
    method: 'GET',
    description: 'Liste des conversations récentes',
    category: 'conversations',
    params: [
      {
        key: 'status',
        label: 'Statut',
        type: 'select',
        options: ['open', 'resolved', 'pending', 'snoozed'],
        default: 'open',
        optional: true,
      },
      {
        key: 'inbox_id',
        label: 'ID Inbox (optionnel)',
        type: 'text',
        optional: true,
      },
      {
        key: 'team_id',
        label: 'ID Équipe (optionnel)',
        type: 'text',
        optional: true,
      },
      {
        key: 'page',
        label: 'Page',
        type: 'number',
        default: 1,
        optional: true,
      },
      {
        key: 'per_page',
        label: 'Par page',
        type: 'number',
        default: 5,
        optional: true,
      },
    ],
    dataMapper: {
      conversations_list: {
        conversations: data => {
          if (data?.data?.payload && Array.isArray(data.data.payload)) {
            return data.data.payload;
          }
          if (data?.payload && Array.isArray(data.payload)) {
            return data.payload;
          }
          if (Array.isArray(data)) {
            return data;
          }
          return [];
        },
      },
    },
  },
  {
    id: 'contacts_list',
    name: 'Liste des contacts',
    endpoint: '/api/v1/accounts/{accountId}/contacts',
    method: 'GET',
    description: 'Liste des contacts récents',
    category: 'contacts',
    params: [
      {
        key: 'page',
        label: 'Page',
        type: 'number',
        default: 1,
        optional: true,
      },
      {
        key: 'per_page',
        label: 'Par page',
        type: 'number',
        default: 5,
        optional: true,
      },
      {
        key: 'sort',
        label: 'Trier par',
        type: 'select',
        options: ['name', 'email', 'created_at', 'last_activity_at'],
        default: 'last_activity_at',
        optional: true,
      },
    ],
    dataMapper: {
      contacts_list: {
        contacts: data => {
          if (data?.data?.payload && Array.isArray(data.data.payload)) {
            return data.data.payload;
          }
          if (data?.payload && Array.isArray(data.payload)) {
            return data.payload;
          }
          if (Array.isArray(data)) {
            return data;
          }
          return [];
        },
      },
    },
  },
  {
    id: 'inboxes_list',
    name: 'Liste des inboxes',
    endpoint: '/api/v1/accounts/{accountId}/inboxes',
    method: 'GET',
    description: 'Liste des inboxes et leur statut',
    category: 'inboxes',
    params: [],
    dataMapper: {
      inbox_status: {
        inboxes: data => {
          if (data?.payload && Array.isArray(data.payload)) {
            return data.payload.map(inbox => ({
              ...inbox,
              status: inbox.enabled ? 'connected' : 'disconnected',
            }));
          }
          if (Array.isArray(data)) {
            return data.map(inbox => ({
              ...inbox,
              status: inbox.enabled ? 'connected' : 'disconnected',
            }));
          }
          return [];
        },
      },
    },
  },
  {
    id: 'conversations_meta',
    name: 'Métadonnées conversations',
    endpoint: '/api/v1/accounts/{accountId}/conversations/meta',
    method: 'GET',
    description: 'Compteurs de conversations',
    category: 'conversations',
    params: [
      {
        key: 'status',
        label: 'Statut',
        type: 'select',
        options: ['all', 'open', 'resolved', 'pending', 'snoozed'],
        default: 'open',
        optional: true,
      },
      {
        key: 'inbox_id',
        label: 'ID Inbox (optionnel)',
        type: 'text',
        optional: true,
      },
      {
        key: 'team_id',
        label: 'ID Équipe (optionnel)',
        type: 'text',
        optional: true,
      },
    ],
    dataMapper: {
      stats_cards: {
        cards: data => {
          const meta = data?.meta || data;
          return [
            {
              label: 'Mes conversations',
              value: formatNumber(meta?.mine_count || 0),
              icon: 'i-lucide-message-square',
              trend: meta?.mine_count > 0 ? 'up' : 'neutral',
            },
            {
              label: 'Non assignées',
              value: formatNumber(meta?.unassigned_count || 0),
              icon: 'i-lucide-alert-circle',
              trend: meta?.unassigned_count > 0 ? 'down' : 'neutral',
            },
            {
              label: 'Assignées',
              value: formatNumber(meta?.assigned_count || 0),
              icon: 'i-lucide-user-check',
              trend: meta?.assigned_count > 0 ? 'up' : 'neutral',
            },
            {
              label: 'Toutes',
              value: formatNumber(meta?.all_count || 0),
              icon: 'i-lucide-inbox',
              trend: 'neutral',
            },
          ];
        },
      },
      metric: {
        value: data => {
          const meta = data?.meta || data;
          return formatNumber(meta?.all_count || meta?.mine_count || 0);
        },
        subvalue: data => {
          const meta = data?.meta || data;
          const unassigned = meta?.unassigned_count || 0;
          return unassigned > 0 ? `${unassigned} non assignées` : null;
        },
        trend: () => 'neutral',
      },
    },
  },
  {
    id: 'orders_list',
    name: 'Liste des commandes',
    endpoint: '/api/v1/accounts/{accountId}/orders',
    method: 'GET',
    description: 'Liste des commandes récentes',
    category: 'orders',
    params: [
      {
        key: 'status',
        label: 'Statut',
        type: 'select',
        options: ['pending', 'processing', 'completed', 'cancelled'],
        optional: true,
      },
      {
        key: 'payment_status',
        label: 'Statut de paiement',
        type: 'select',
        options: ['pending', 'paid', 'failed', 'refunded'],
        optional: true,
      },
      {
        key: 'page',
        label: 'Page',
        type: 'number',
        default: 1,
        optional: true,
      },
      {
        key: 'per_page',
        label: 'Par page',
        type: 'number',
        default: 5,
        optional: true,
      },
    ],
    dataMapper: {
      orders_list: {
        orders: data => {
          if (Array.isArray(data)) {
            return data;
          }
          if (data?.data && Array.isArray(data.data)) {
            return data.data;
          }
          if (data?.payload && Array.isArray(data.payload)) {
            return data.payload;
          }
          return [];
        },
      },
      metric: {
        value: data => {
          const orders = Array.isArray(data)
            ? data
            : data?.data || data?.payload || [];
          return formatNumber(orders.length);
        },
        subvalue: data => {
          const orders = Array.isArray(data)
            ? data
            : data?.data || data?.payload || [];
          const total = orders.reduce(
            (sum, order) => sum + (parseFloat(order.total) || 0),
            0
          );
          return `Total: ${formatPrice(total)}`;
        },
        trend: () => 'neutral',
      },
      stats_cards: {
        cards: data => {
          const orders = Array.isArray(data)
            ? data
            : data?.data || data?.payload || [];
          const pending = orders.filter(o => o.status === 'pending').length;
          const processing = orders.filter(
            o => o.status === 'processing'
          ).length;
          const completed = orders.filter(o => o.status === 'completed').length;
          const total = orders.reduce(
            (sum, order) => sum + (parseFloat(order.total) || 0),
            0
          );

          return [
            {
              label: 'En attente',
              value: formatNumber(pending),
              icon: 'i-lucide-clock',
              trend: pending > 0 ? 'up' : 'neutral',
            },
            {
              label: 'En traitement',
              value: formatNumber(processing),
              icon: 'i-lucide-loader',
              trend: processing > 0 ? 'up' : 'neutral',
            },
            {
              label: 'Terminées',
              value: formatNumber(completed),
              icon: 'i-lucide-check-circle',
              trend: completed > 0 ? 'up' : 'neutral',
            },
            {
              label: 'Total',
              value: formatPrice(total),
              icon: 'i-lucide-euro',
              trend: 'neutral',
            },
          ];
        },
      },
      chart: {
        series: data => {
          const orders = Array.isArray(data)
            ? data
            : data?.data || data?.payload || [];
          const byStatus = {
            pending: orders.filter(o => o.status === 'pending').length,
            processing: orders.filter(o => o.status === 'processing').length,
            completed: orders.filter(o => o.status === 'completed').length,
            cancelled: orders.filter(o => o.status === 'cancelled').length,
          };
          return [
            {
              name: 'Commandes',
              data: [
                byStatus.pending,
                byStatus.processing,
                byStatus.completed,
                byStatus.cancelled,
              ],
            },
          ];
        },
        categories: () => [
          'En attente',
          'En traitement',
          'Terminées',
          'Annulées',
        ],
      },
      delivery_status: {
        orders: data => {
          if (Array.isArray(data)) {
            return data;
          }
          if (data?.data && Array.isArray(data.data)) {
            return data.data;
          }
          if (data?.payload && Array.isArray(data.payload)) {
            return data.payload;
          }
          return [];
        },
      },
    },
  },
  {
    id: 'orders_summary',
    name: 'Résumé des commandes',
    endpoint: '/api/v1/accounts/{accountId}/orders',
    method: 'GET',
    description: 'Métriques globales des commandes',
    category: 'orders',
    params: [
      {
        key: 'status',
        label: 'Statut',
        type: 'select',
        options: ['pending', 'processing', 'completed', 'cancelled'],
        optional: true,
      },
    ],
    dataMapper: {
      metric: {
        value: data => {
          const orders = Array.isArray(data)
            ? data
            : data?.data || data?.payload || [];
          const total = orders.reduce(
            (sum, order) => sum + (parseFloat(order.total) || 0),
            0
          );
          return formatPrice(total);
        },
        subvalue: data => {
          const orders = Array.isArray(data)
            ? data
            : data?.data || data?.payload || [];
          return `${orders.length} commande${orders.length > 1 ? 's' : ''}`;
        },
        trend: () => 'neutral',
      },
    },
  },
  {
    id: 'drive_files',
    name: 'Fichiers du Drive',
    endpoint: '/api/v1/accounts/{accountId}/drive_files',
    method: 'GET',
    description: 'Liste des fichiers du Drive',
    category: 'drive',
    params: [
      {
        key: 'folder_path',
        label: 'Chemin du dossier',
        type: 'text',
        optional: true,
      },
      {
        key: 'file_type',
        label: 'Type de fichier',
        type: 'select',
        options: [
          'all',
          'image',
          'video',
          'audio',
          'document',
          'pdf',
          'text',
          'spreadsheet',
          'archive',
          'code',
        ],
        optional: true,
      },
      {
        key: 'search',
        label: 'Recherche',
        type: 'text',
        optional: true,
      },
      {
        key: 'sort_by',
        label: 'Trier par',
        type: 'select',
        options: ['name', 'size', 'type', 'updated_at'],
        default: 'updated_at',
        optional: true,
      },
      {
        key: 'sort_order',
        label: 'Ordre',
        type: 'select',
        options: ['asc', 'desc'],
        default: 'desc',
        optional: true,
      },
    ],
    dataMapper: {
      drive: {
        items: data => {
          if (Array.isArray(data)) {
            return data;
          }
          if (data?.data && Array.isArray(data.data)) {
            return data.data;
          }
          if (data?.payload && Array.isArray(data.payload)) {
            return data.payload;
          }
          return [];
        },
      },
    },
  },
  {
    id: 'custom',
    name: 'Endpoint personnalisé',
    endpoint: '',
    method: 'GET',
    description: 'Configurez votre propre endpoint API',
    category: 'custom',
    params: [],
    dataMapper: {},
  },
];

// Fonctions utilitaires de formatage
export const formatNumber = num => {
  if (typeof num !== 'number') {
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return '0';
    num = parsed;
  }
  return num.toLocaleString('fr-FR');
};

export const formatDate = date => {
  if (!date) return '';
  const d = typeof date === 'number' ? new Date(date * 1000) : new Date(date);
  if (isNaN(d.getTime())) return String(date);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
};

export const formatPrice = price => {
  if (!price) return '€0.00';
  const num =
    typeof price === 'string' ? parseFloat(price.replace(/\s/g, '')) : price;
  return `€${typeof num === 'number' ? num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : price}`;
};

// Fonction pour remplacer les placeholders dans l'endpoint
export const buildEndpointUrl = (endpoint, accountId, params = {}) => {
  let url = endpoint.replace('{accountId}', accountId);

  // Ajouter les paramètres de requête
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `${url}?${queryString}` : url;
};

// Fonction pour mapper les données selon le mapper configuré
export const mapDataToWidget = (data, mapper, widgetType) => {
  if (!mapper || !mapper[widgetType]) {
    // Pas de mapper spécifique, retourner les données brutes
    return data;
  }

  const widgetMapper = mapper[widgetType];
  const mapped = {};

  Object.entries(widgetMapper).forEach(([key, mapperFn]) => {
    try {
      if (typeof mapperFn === 'function') {
        // Mapper est une fonction, l'exécuter
        mapped[key] = mapperFn(data);
      } else if (typeof mapperFn === 'string') {
        // Mapper est un chemin de propriété
        mapped[key] = getNestedValue(data, mapperFn);
      } else {
        // Valeur statique
        mapped[key] = mapperFn;
      }
    } catch (e) {
      console.warn(`Erreur lors du mapping de ${key}:`, e);
      mapped[key] = undefined;
    }
  });

  // Nettoyer les valeurs undefined
  Object.keys(mapped).forEach(key => {
    if (mapped[key] === undefined) {
      delete mapped[key];
    }
  });

  return mapped;
};

// Fonction utilitaire pour accéder aux valeurs imbriquées
const getNestedValue = (obj, path) => {
  if (!path || !obj) return undefined;
  try {
    return path.split('.').reduce((current, key) => {
      if (current === null || current === undefined) return undefined;
      return current[key];
    }, obj);
  } catch (e) {
    return undefined;
  }
};

// Fonction pour récupérer les données depuis l'API
export const fetchApiData = async (endpointConfig, accountId, params = {}) => {
  const url = buildEndpointUrl(endpointConfig.endpoint, accountId, params);

  try {
    const response = await fetch(url, {
      method: endpointConfig.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        api_access_token: window.authToken || '',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Erreur lors de la récupération des données:', error);
    throw error;
  }
};
